import React from 'react';
import { Rocket, Factory, CheckCircle2, ArrowRight } from 'lucide-react';

interface ServicesProps {
    onNavigate: (page: 'home' | 'blog' | 'contact' | 'resources' | 'products', sectionId?: string) => void;
}

const Services: React.FC<ServicesProps> = ({ onNavigate }) => {
  return (
    <section id="services" className="py-16 md:py-24 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-orange-50 border border-orange-100 mb-6">
            <span className="text-orange-800 text-xs font-bold uppercase tracking-widest">
                Manufacturing Solutions
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            Scalable Solutions for Startups & Global Brands
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Whether you are an early-stage founder, a growing D2C brand, or an established company, we have a manufacturing path for&nbsp;you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Path 1: Brand Launch */}
          <div className="bg-white rounded-3xl p-6 md:p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group border border-gray-100 flex flex-col h-full">
            {/* Slick Left Frame - Orange */}
            <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <div className="mb-8">
              <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-orange-100">
                <Rocket className="w-8 h-8 text-orange-600" strokeWidth={2} />
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">Brand Launch Program</h3>
              <div className="inline-block bg-orange-50 px-4 py-2 rounded-lg border border-orange-100">
                 <p className="text-orange-900 font-bold text-xs md:text-sm">"I want to launch a private label brand"</p>
              </div>
            </div>
            
            <div className="flex-1 flex flex-col">
              <div className="mb-8">
                <h4 className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-4">A Turnkey System For</h4>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-700 font-medium text-sm md:text-base">
                    <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-3"></span>Startup Founders & D2C Brands
                  </li>
                  <li className="flex items-center text-gray-700 font-medium text-sm md:text-base">
                    <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-3"></span>Fitness Influencers & Coaches
                  </li>
                  <li className="flex items-center text-gray-700 font-medium text-sm md:text-base">
                    <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-3"></span>Amazon & Flipkart Sellers
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-500/[0.06] to-orange-500/[0.02] rounded-2xl p-6 border border-orange-100 mb-8">
                <div className="flex justify-between items-end mb-3">
                  <span className="font-bold text-gray-900 text-base md:text-lg">Timeline to Market</span>
                  <span className="text-orange-600 font-bold text-xl md:text-2xl">45-60 Days</span>
                </div>
                <div className="w-full bg-white rounded-full h-2.5 shadow-inner border border-orange-100">
                  <div className="bg-orange-500 h-full rounded-full w-full shadow-sm"></div>
                </div>
              </div>

              <div className="mt-auto">
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <div className="bg-green-100 p-0.5 rounded-full mr-3 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-600 text-sm font-medium">Formulation & Flavor Development</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-100 p-0.5 rounded-full mr-3 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-600 text-sm font-medium">FSSAI Licensing & Label Compliance</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-100 p-0.5 rounded-full mr-3 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-600 text-sm font-medium">Low MOQ (Minimum Order Quantity)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-100 p-0.5 rounded-full mr-3 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-600 text-sm font-medium">Complete Packaging Solutions (Design, Containers, Labels)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-100 p-0.5 rounded-full mr-3 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-600 text-sm font-medium">Website, Amazon, 1MG Listing & Fulfillment</span>
                  </li>
                </ul>
                <button 
                  onClick={() => onNavigate('contact')}
                  className="w-full py-4 rounded-xl bg-orange-500 text-black font-bold text-lg hover:bg-orange-600 transition-all shadow-lg hover:shadow-orange-200 flex items-center justify-center group-hover:scale-[1.02]"
                >
                  Start Your Brand <ArrowRight className="ml-2 w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Path 2: Contract Manufacturing */}
          <div className="bg-white rounded-3xl p-6 md:p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group border border-gray-100 flex flex-col h-full">
            {/* Slick Left Frame - Black */}
            <div className="absolute top-0 bottom-0 left-0 w-1.5 bg-black opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <div className="mb-8">
              <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-gray-100">
                <Factory className="w-8 h-8 text-black" strokeWidth={2} />
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">Contract Manufacturing</h3>
              <div className="inline-block bg-gray-100 px-4 py-2 rounded-lg border border-gray-200">
                 <p className="text-gray-700 font-bold text-xs md:text-sm">"I need a high-volume manufacturing partner"</p>
              </div>
            </div>
            
            <div className="flex-1 flex flex-col">
              <div className="mb-8">
                <h4 className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-4">Perfect For</h4>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-700 font-medium text-sm md:text-base">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-3"></span>Established D2C Brands
                  </li>
                  <li className="flex items-center text-gray-700 font-medium text-sm md:text-base">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-3"></span>Global Brands & Exporters
                  </li>
                  <li className="flex items-center text-gray-700 font-medium text-sm md:text-base">
                    <span className="w-1.5 h-1.5 bg-black rounded-full mr-3"></span>Retail Chains & Distributors
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-gray-500/[0.08] to-gray-500/[0.02] rounded-2xl p-6 border border-gray-100 mb-8">
                 <div className="flex flex-col space-y-2">
                    <span className="text-gray-500 text-xs font-bold uppercase tracking-wider">Production Capacity</span>
                    <div className="font-bold text-gray-900 text-lg md:text-xl">10M+ Units Annually</div>
                    <p className="text-sm text-gray-500 font-medium">Scalable for rapid growth.</p>
                 </div>
              </div>

              <div className="mt-auto">
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <div className="bg-green-100 p-0.5 rounded-full mr-3 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-600 text-sm font-medium">FSSAI, GMP, ISO & HACCP Certified</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-100 p-0.5 rounded-full mr-3 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-600 text-sm font-medium">Competitive Pricing (Domestic & Export)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-100 p-0.5 rounded-full mr-3 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-600 text-sm font-medium">Complete QA/QC with COAs for every batch</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-100 p-0.5 rounded-full mr-3 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-600 text-sm font-medium">Pan-India Logistics & Export Support</span>
                  </li>
                </ul>
                <button 
                  onClick={() => onNavigate('contact')}
                  className="w-full py-4 rounded-xl bg-black text-white font-bold text-lg hover:bg-gray-800 transition-all shadow-lg hover:shadow-gray-300 flex items-center justify-center group-hover:scale-[1.02]"
                >
                  Request Quote <ArrowRight className="ml-2 w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;